import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, ChevronDown, Building2, Calendar, Clock, TrendingUp, Briefcase, Activity, Bell, DollarSign, BarChart2, ShieldCheck, Timer, Contact, Sparkles } from 'lucide-react';
import useClientSummary from './hooks/useClientSummary';
import WidgetCustomizationModal from './WidgetCustomizationModal';

const isIBRole = () => (sessionStorage.getItem('role') || '').toLowerCase() === 'investment banker';

const DEFAULT_SETTINGS = {
  summaryPrompt: "Generate a concise summary of the client's portfolio and key insights. Include one short sentence summary and a more detailed 2-3 sentence analysis.",
  showShortSummary: true,
  showLongSummary: true,
  metrics: {
    aum: true, ytdReturn: true, timeHorizon: true, lastContact: true,
    riskTolerance: false, investmentGoal: false, nextReview: false, clientSince: false
  },
  tooltips: {
    aum: 'Assets Under Management', ytdReturn: 'Year-to-Date Return',
    timeHorizon: 'Investment Time Horizon', lastContact: 'Last Contact Date',
    riskTolerance: 'Risk Tolerance', investmentGoal: 'Investment Goal',
    nextReview: 'Next Review Date', clientSince: 'Client Since'
  },
  headerColor: '#ffffff', textColor: '#000000'
};

const IB_DEFAULT_SETTINGS = {
  summaryPrompt: "Generate a concise brief on this financial institution as a financial institution client. Include one short sentence overview and a 2-3 sentence analysis covering: current wallet share, KYC status, active deal pipeline, recent engagement level, and key opportunities or risks for the upcoming coverage call.",
  showShortSummary: true, showLongSummary: true,
  metrics: { proxyYtd: true, pipelineWeight: true, engagementScore: true, openAlerts: true },
  tooltips: {
    proxyYtd: 'Year-to-date benchmark proxy performance.',
    pipelineWeight: 'Total value of live and prospective deals in pipeline.',
    engagementScore: 'Engagement score across recent interactions.',
    openAlerts: 'Number of open action items or compliance flags.'
  },
  headerColor: '#ffffff', textColor: '#000000'
};

const SlimClientProfile = ({
  clientData, allClients, onClientChange, useCaseId,
  widgetContents = {}, allWidgetsLoaded = false,
  onNavigateBack, useCases = [], selectedUseCase, onUseCaseChange,
  dataLoading = false
}) => {
  const ibRole = isIBRole();
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [summaryGeneratedAfterLoad, setSummaryGeneratedAfterLoad] = useState(false);

  const savedKey = ibRole ? 'ibClientProfileWidgetSettings' : 'clientProfileWidgetSettings';
  const [widgetSettings, setWidgetSettings] = useState(() => {
    const defaults = ibRole ? IB_DEFAULT_SETTINGS : DEFAULT_SETTINGS;
    try {
      const saved = localStorage.getItem(savedKey);
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch (e) { /* ignore */ }
    return defaults;
  });

  const { loading, summary, noClientsAvailable, isLoadingCustomerData, customerData, fetchSummary } = useClientSummary({
    widgetContents, clientData, allClients, widgetSettings, useCaseId,
    agentId: '63492', domainId: null, skipInitialFetch: !allWidgetsLoaded, isIBRole: ibRole
  });

  useEffect(() => {
    localStorage.setItem(savedKey, JSON.stringify(widgetSettings));
  }, [widgetSettings, savedKey]);

  useEffect(() => {
    if (allWidgetsLoaded && !summaryGeneratedAfterLoad && Object.keys(widgetContents).length > 0) {
      fetchSummary(true);
      setSummaryGeneratedAfterLoad(true);
    }
  }, [allWidgetsLoaded, summaryGeneratedAfterLoad, fetchSummary, widgetContents]);

  useEffect(() => {
    if (Object.keys(widgetContents).length === 0) setSummaryGeneratedAfterLoad(false);
  }, [widgetContents]);

  const display = customerData || clientData;
  const nextMeeting = display?.next_meeting;

  const rawShortSummary = summary?.shortSummary || '';

  const hasError = rawShortSummary && (
    rawShortSummary.toLowerCase().includes('error') ||
    rawShortSummary.toLowerCase().includes('cannot answer') ||
    rawShortSummary.toLowerCase().includes('?') ||
    rawShortSummary.toLowerCase().includes('sorry') ||
    rawShortSummary.toLowerCase().includes("don't know") ||
    rawShortSummary.toLowerCase().includes('safely') ||
    rawShortSummary.toLowerCase().includes("i'm an expert")
  );

  const generateFallbackShortSummary = () => {
    if (!display) return ibRole ? 'Institution brief not available.' : 'Client summary not available.';
    if (ibRole) {
      const name = display.name || 'This institution';
      const pw = parseFloat(display.pipeline_weight);
      const es = parseFloat(display.engagement_score);
      const alerts = parseInt(display.open_alerts, 10);
      const pipelineFmt = !isNaN(pw) && pw > 0
        ? (pw >= 1e9 ? `$${(pw / 1e9).toFixed(1)}B` : pw >= 1e6 ? `$${(pw / 1e6).toFixed(1)}M` : `$${pw.toFixed(0)}`)
        : null;
      const urgency = (!isNaN(alerts) && alerts > 0) ? 'action needed'
        : (!isNaN(es) && es < 4) ? 'low engagement — re-engage'
          : (!isNaN(es) && es >= 7) ? 'strong momentum'
            : 'active coverage';
      return pipelineFmt ? `${name} · ${pipelineFmt} pipeline · ${urgency}` : `${name} · ${urgency}`;
    }
    const highlights = [];
    if (display.name) highlights.push(`${display.name.split(' ')[0]}`);
    if (display.net_worth_category) highlights.push(`${display.net_worth_category.toLowerCase()}-net-worth client`);
    if (display.total_aum) highlights.push(`$${(display.total_aum / 1000000).toFixed(2)}M AUM`);
    if (display.ytd_return) highlights.push(`${display.ytd_return}% YTD return`);
    if (display.risk_tolerance) highlights.push(`${display.risk_tolerance.toLowerCase()} risk tolerance`);
    if (display.time_horizon_category) highlights.push(`${display.time_horizon_category.toLowerCase()} horizon`);
    return highlights.length > 0
      ? `${highlights.join(', ')} requiring focused attention.`
      : 'Client summary not available.';
  };

  const rawLongSummary = summary?.longSummary || '';
  const hasLongError = rawLongSummary && (
    rawLongSummary.toLowerCase().includes('error') ||
    rawLongSummary.toLowerCase().includes('cannot answer') ||
    rawLongSummary.toLowerCase().includes('?') ||
    rawLongSummary.toLowerCase().includes('sorry') ||
    rawLongSummary.toLowerCase().includes("don't know") ||
    rawLongSummary.toLowerCase().includes('safely') ||
    rawLongSummary.toLowerCase().includes("i'm an expert")
  );

  const generateFallbackLongSummary = () => {
    if (!display) return 'Detailed client information not available.';
    if (ibRole) {
      const fmt = (val) => {
        const n = parseFloat(val);
        if (isNaN(n) || n <= 0) return null;
        if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
        if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
        return `$${n.toLocaleString()}`;
      };
      const name = display.name || 'This institution';
      const pw = fmt(display.pipeline_weight);
      const es = parseFloat(display.engagement_score);
      const alerts = parseInt(display.open_alerts, 10);
      const engagementLabel = !isNaN(es)
        ? (es >= 7 ? 'a high-engagement account with strong momentum'
          : es >= 4 ? 'a mid-tier engagement account with room to grow'
          : 'a low-engagement account flagged for re-activation')
        : 'a key coverage account';
      let narrative = `${name} is ${engagementLabel}.`;
      if (pw) narrative += ` The institution carries ${pw} in active pipeline.`;
      if (!isNaN(alerts) && alerts > 0)
        narrative += ` ${alerts} open alert${alerts > 1 ? 's require' : ' requires'} immediate follow-up before the next call.`;
      if (display.last_contact_date) narrative += ` Last touch: ${display.last_contact_date}.`;
      if (display.next_meeting?.date) narrative += ` Next coverage call: ${display.next_meeting.date}.`;
      return narrative;
    }
    const details = [];
    if (display.name && display.age) details.push(`${display.name} (${display.age})`);
    else if (display.name) details.push(display.name);
    const financialDetails = [];
    if (display.net_worth) financialDetails.push(`net worth of $${(display.net_worth / 1e6).toFixed(2)}M`);
    if (display.total_aum) financialDetails.push(`$${(display.total_aum / 1e6).toFixed(2)}M AUM`);
    if (display.ytd_return) financialDetails.push(`${display.ytd_return}% YTD return`);
    if (financialDetails.length > 0) details.push(`has ${financialDetails.join(' with ')}`);
    if (display.risk_tolerance && display.time_horizon_category)
      details.push(`demonstrates ${display.risk_tolerance.toLowerCase()} risk tolerance with a ${display.time_horizon_category.toLowerCase()} investment horizon`);
    else if (display.risk_tolerance) details.push(`has ${display.risk_tolerance.toLowerCase()} risk tolerance`);
    else if (display.time_horizon_category) details.push(`has a ${display.time_horizon_category.toLowerCase()} investment horizon`);
    if (display.last_contact_date) details.push(`last contacted on ${display.last_contact_date}`);
    return details.length > 0 ? `${details.join(', ')}.` : 'Detailed client information not available.';
  };

  const toGBP = (text) => text?.replace(/\$/g, '£') ?? text;
  const resolvedShortSummary = hasError ? generateFallbackShortSummary() : rawShortSummary;
  const shortSummary = ibRole ? toGBP(resolvedShortSummary) : resolvedShortSummary;
  const resolvedLongSummary = hasLongError ? generateFallbackLongSummary() : rawLongSummary;
  const longSummary = ibRole ? toGBP(resolvedLongSummary) : resolvedLongSummary;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const metrics = ibRole ? [
    { label: 'Proxy YTD', value: display?.proxy_ytd ?? display?.ytd_return ?? 'N/A', icon: TrendingUp },
    { label: 'Pipeline', value: display?.pipeline_weight ? `£${(display.pipeline_weight / 1e6).toFixed(1)}M` : 'N/A', icon: Briefcase },
    { label: 'Engagement', value: display?.engagement_score ?? 'N/A', icon: Activity },
    { label: 'Alerts', value: display?.open_alerts ?? 'N/A', icon: Bell },
  ] : [
    { label: 'AUM', value: display?.total_aum ? `$${(display.total_aum / 1e6).toFixed(2)}M` : 'N/A', icon: DollarSign },
    { label: 'YTD Return', value: display?.ytd_return ? `${display.ytd_return}%` : 'N/A', icon: BarChart2 },
    { label: 'Risk', value: display?.risk_tolerance || 'N/A', icon: ShieldCheck },
    { label: 'Horizon', value: display?.time_horizon_category || 'N/A', icon: Timer },
    { label: 'Last Contact', value: display?.last_contact_date || 'N/A', icon: Contact },
  ];

  if (isLoadingCustomerData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 min-h-[80vh]">
        <div className="w-8 h-8 rounded-full border-2 border-t-primary border-primaryLight animate-spin" />
        <p className="mt-3 text-sm text-gray-500">Loading client data...</p>
      </div>
    );
  }

  if (noClientsAvailable) {
    return (
      <div className="flex flex-col items-center justify-center py-12 min-h-[80vh] px-4 text-center">
        <p className="text-gray-500 text-sm">No clients available.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white">

      {/* Top controls: use case select */}
      <div className="flex items-center gap-2 px-3 py-2.5 sticky top-0 bg-white z-10 border-b border-gray-100">
        <select
          className="flex-1 text-xs border border-gray-200 rounded bg-gray-50 text-[#374151] cursor-pointer focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 px-2 py-1.5 min-w-0 transition-all duration-300"
          onChange={onUseCaseChange}
          value={selectedUseCase}
        >
          {useCases.map(uc => <option key={uc.id} value={uc.id}>{uc.name}</option>)}
        </select>
      </div>

      {/* Avatar & name */}
      <div className="flex items-center py-2.5 px-3 border-b border-gray-100">
        {display?.img ? (
          <img
            src={display.img}
            alt={display?.name || 'Client'}
            className={`w-12 h-12 shrink-0 object-cover shadow-[0_4px_6px_rgba(0,0,0,0.1)] transition-all duration-300 hover:scale-[1.02] ${ibRole ? 'rounded-xl' : 'rounded-full'}`}
          />
        ) : ibRole ? (
          <div className="w-12 h-12 shrink-0 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shadow-[0_4px_6px_rgba(0,0,0,0.07)]">
            {display?.name
              ? <span className="text-base font-semibold text-primary">{getInitials(display.name)}</span>
              : <Building2 className="w-5 h-5 text-gray-400" />}
          </div>
        ) : (
          <div
            className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-white font-bold text-base shadow-[0_4px_6px_rgba(0,0,0,0.12)] transition-all duration-300 hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
          >
            {getInitials(display?.name)}
          </div>
        )}

        {/* Name with inline dropdown */}
        <div className="min-w-0 flex-1 relative ml-2.5">
          <button
            onClick={() => allClients.length > 1 && setShowClientDropdown(v => !v)}
            className={`w-full flex items-center gap-1 text-sm font-bold text-[#374151] leading-tight mb-1 transition-all duration-300 ${allClients.length > 1 ? 'cursor-pointer hover:text-primary' : 'cursor-default'}`}
          >
            <span className="truncate">{display?.name || 'Select Client'}</span>
            {allClients.length > 1 && (
              <ChevronDown size={13} className={`shrink-0 text-gray-400 transition-transform duration-300 ${showClientDropdown ? 'rotate-180' : ''}`} />
            )}
          </button>
          {display?.net_worth_category && (
            <div className="text-xs text-gray-400 mb-1" style={{ lineHeight: '1.5' }}>{display.net_worth_category} Net Worth Individual</div>
          )}



          {showClientDropdown && allClients.length > 1 && (
            <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.1)] z-20 max-h-48 overflow-y-auto">
              {allClients.map(c => (
                <button
                  key={c.id}
                  onClick={() => { onClientChange(c); setShowClientDropdown(false); }}
                  className={`w-full text-left px-3 py-2.5 text-xs transition-all duration-200 ${c.id === display?.id ? 'text-primary font-semibold bg-primary/5' : 'text-[#374151] hover:bg-gray-50'}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {(shortSummary || longSummary || loading) && (widgetSettings.showShortSummary || widgetSettings.showLongSummary) && (
        <div className="px-3 py-2.5 border-b border-gray-100 flex flex-col gap-1.5 max-h-[22vh] overflow-y-auto [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
          {loading ? (
            <div className="flex items-center gap-1.5 py-1">
              <Sparkles size={12} className="text-primary animate-pulse shrink-0" />
              <span className="text-xs text-gray-400 italic">
                {ibRole ? 'Generating institution brief...' : 'Generating client insights...'}
              </span>
            </div>
          ) : (
            <>
              {shortSummary && widgetSettings.showShortSummary && (
                <p className="text-xs text-secondary italic font-medium" style={{ lineHeight: '1.65' }}>{shortSummary}</p>
              )}
              {longSummary && widgetSettings.showLongSummary && (
                <p className="text-xs text-gray-500" style={{ lineHeight: '1.65' }}>{longSummary}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Next Call */}
      {!dataLoading && nextMeeting && (
        <div className="px-3 py-2.5 border-b border-gray-100 flex flex-col gap-1.5">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider">Next Call</div>
          <div className="flex items-center gap-2 text-xs text-[#374151]">
            <Calendar size={11} className="text-primaryDark shrink-0" />
            <span style={{ lineHeight: '1.5' }}>{nextMeeting.date}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#374151]">
            <Clock size={11} className="text-primaryDark shrink-0" />
            <span style={{ lineHeight: '1.5' }}>{nextMeeting.start_time} ({nextMeeting.duration} min)</span>
          </div>
        </div>
      )}

      {/* Contact info */}
      {(display?.email || display?.phone || display?.address || display?.location) && (
        <div className="px-3 py-2.5 border-b border-gray-100 flex flex-col gap-2">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider">Contact</div>
          {display?.email && (
            <div className="flex items-center gap-2 transition-all duration-200 hover:translate-x-0.5">
              <Mail size={11} className="text-primaryDark shrink-0" />
              <span className="truncate text-xs text-[#374151]" style={{ lineHeight: '1.5' }}>{display.email}</span>
            </div>
          )}
          {display?.phone && (
            <div className="flex items-center gap-2 transition-all duration-200 hover:translate-x-0.5">
              <Phone size={11} className="text-primaryDark shrink-0" />
              <span className="truncate text-xs text-[#374151]" style={{ lineHeight: '1.5' }}>{display.phone}</span>
            </div>
          )}
          {(display?.address || display?.location) && (
            <div className="flex items-center gap-2 transition-all duration-200 hover:translate-x-0.5">
              <MapPin size={11} className="text-primaryDark shrink-0" />
              <span className="truncate text-xs text-[#374151]" style={{ lineHeight: '1.5' }}>{display.address || display.location}</span>
            </div>
          )}
        </div>
      )}

      {/* Metrics */}
      <div className="px-3 py-2.5 flex flex-col gap-2">
        <div className="text-xs font-semibold text-primary uppercase tracking-wider">Overview</div>
        {metrics.map((m, i) => (
          <div key={i} className="flex items-center justify-between transition-all duration-200 hover:translate-x-0.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <m.icon size={10} className="text-primaryDark shrink-0" />
              <span className="text-xs text-gray-500 truncate" style={{ lineHeight: '1.5' }}>{m.label}</span>
            </div>
            <span className="text-xs font-semibold text-[#374151] ml-1 shrink-0">{m.value}</span>
          </div>
        ))}
      </div>

      {showCustomizationModal && (
        <WidgetCustomizationModal
          widgetSettings={widgetSettings}
          onSave={(newSettings) => {
            const promptChanged = newSettings.summaryPrompt !== widgetSettings.summaryPrompt;
            setWidgetSettings(newSettings);
            if (promptChanged && Object.keys(widgetContents).length > 0) {
              setTimeout(() => fetchSummary(true), 300);
            }
          }}
          onClose={() => setShowCustomizationModal(false)}
          isIBRole={ibRole}
        />
      )}
    </div>
  );
};

export default SlimClientProfile;
